// Route normalization + page naming for page_viewed.
//
// Two jobs: (1) collapse dynamic path segments to a stable template so
// analytics groups by page, not by id — driven off the App.jsx route table,
// not a blind regex, so a new static route logs as-is rather than being
// mangled; (2) SCRUB share/invite tokens so a raw capability token never lands
// in an event.

// Order matters, and the token rules come FIRST: a share link like
// /ai/chat/s/<token> also matches the /ai/chat family below, so scrubbing has
// to win the race or the token would be swallowed into the chat surface.
//
// The DashAI rules carry the /ai prefix because that is where the routes are
// mounted (DashAIRoutes.tsx under App.jsx's /ai/* element). The unprefixed
// twins below them are the legacy redirect routes, which normalize to the same
// surface so a redirect that manages to emit lands in the same bucket rather
// than opening a phantom row.
const RULES: Array<[RegExp, string]> = [
  // Token-bearing routes — the token is scrubbed to a placeholder.
  [/^\/join\/[^/]+/, '/join/:token'],
  [/^\/ai\/canvas\/s\/[^/]+/, '/ai/canvas/s/:token'],
  [/^\/ai\/insights\/s\/[^/]+/, '/ai/insights/s/:token'],
  [/^\/ai\/chat\/s\/[^/]+/, '/ai/chat/s/:token'],
  // DashAI surfaces. Chat collapses the whole family — session and canvas
  // alike — to one surface key, so the leaderboard reports the chat page
  // rather than one row per conversation.
  [/^\/ai\/chat(\/|$)/, '/ai/chat'],
  // No `$` alternative on the legacy twin: `/chat` alone was never a route
  // (App.jsx mounts only /chat/:chatId and /chat/:chatId/:canvasId).
  [/^\/chat\//, '/ai/chat'],
  [/^\/ai\/autonomous\/[^/]+/, '/ai/autonomous/:sessionId'],
  [/^\/ai\/workflow-agents\/[^/]+/, '/ai/workflow-agents/:agentId'],
  [/^\/workflow-agents\/[^/]+/, '/ai/workflow-agents/:agentId'],
  [/^\/ai\/automated-reporting\/[^/]+/, '/ai/automated-reporting/:reportId'],
  [/^\/automated-reporting\/[^/]+/, '/ai/automated-reporting/:reportId'],
  [/^\/ai\/automated-insights\/[^/]+/, '/ai/automated-insights/:insightId'],
  [/^\/automated-insights\/[^/]+/, '/ai/automated-insights/:insightId'],
  [/^\/ai\/saved-canvas\/[^/]+/, '/ai/saved-canvas/:tab'],
  [/^\/saved-canvas\/[^/]+/, '/ai/saved-canvas/:tab'],
  // Dashboard surfaces.
  [/^\/outlet\/[^/]+/, '/outlet/:outletid'],
  [/^\/my_list\/[^/]+/, '/my_list/:listId'],
  // Legacy hyphen spelling of the Market Overview route (the live route is
  // `/market_overview`), collapsed so the two do not split into two rows.
  [/^\/market-overview(\/|$)/, '/market_overview'],
];

export function normalizePath(pathname: string): string {
  for (const [re, repl] of RULES) if (re.test(pathname)) return repl;
  return pathname;
}

// Human-readable page names (superset of GeneralLayout's DASHBOARD_NAMES).
// Keyed on the NORMALIZED path. Unknown paths return undefined (page_name is
// optional) — the normalized `page` is still recorded.
const PAGE_NAMES: Record<string, string> = {
  '/': 'Home',
  '/market_overview': 'Market Overview',
  '/market_deepdive': 'Market Deepdive',
  '/execution_view': 'Execution View',
  '/physical_location': 'Physical Location',
  '/physical_franchise': 'Physical Franchise',
  '/accounts': 'Accounts',
  '/key_accounts': 'Key Accounts',
  '/global_key_accounts': 'Global Key Accounts',
  '/my_list': 'My List',
  '/my_list/:listId': 'My List Details',
  '/outlet/:outletid': 'Outlet Details',
  '/user_management': 'User Management',
  '/glossary': 'Glossary',
  '/video_tutorial': 'Video Tutorial',
  '/learn': 'Learn Hub',
  '/settings': 'Settings',
  '/ai': 'DashAI',
  '/ai/chat': 'DashAI Chat',
  // Share-link view surfaces (the token is scrubbed to :token by the RULES above).
  '/ai/chat/s/:token': 'Shared Chat',
  '/ai/canvas/s/:token': 'Shared Canvas',
  '/ai/insights/s/:token': 'Shared Insight',
  '/ai/autonomous/:sessionId': 'Autonomous Session',
  // Each of these surfaces has both a bare landing route and an :id route
  // (DashAIRoutes.tsx), and the bare one is static so it reaches pageName
  // un-normalized. Both spellings map to the same label, otherwise the landing
  // row renders a raw path next to its named sibling.
  '/ai/workflow-agents': 'Workflow Agents',
  '/ai/workflow-agents/:agentId': 'Workflow Agents',
  '/ai/automated-reporting': 'Automated Reporting',
  '/ai/automated-reporting/:reportId': 'Automated Reporting',
  '/ai/automated-insights': 'Automated Insights',
  '/ai/automated-insights/:insightId': 'Automated Insights',
  '/ai/saved-canvas': 'Saved Canvas',
  '/ai/saved-canvas/:tab': 'Saved Canvas',
  '/ai/profile': 'DashAI Profile',
  '/ai/account': 'DashAI Account',
};

export function pageName(normalizedPath: string): string | undefined {
  return PAGE_NAMES[normalizedPath];
}
